import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  PanelLeft,
  ChevronsLeft,
  EyeOff,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore.js';
import { cn } from '../../lib/utils.js';
import { useModules } from '../../hooks/useModules.js';
import { ModuleIcon } from '../icons/ModuleIcon.js';

// Sidebar reads its module list from atlas via useModules() (#43).
// Dashboard is the only static entry — it's the meta-overview, not a
// platform module.

const toggleIcons = {
  expanded: ChevronsLeft,
  collapsed: EyeOff,
  hidden: PanelLeft,
};

const toggleTitles = {
  expanded: 'Daralt',
  collapsed: 'Gizle',
  hidden: 'Genişlet',
};

export function Sidebar() {
  const { sidebarState, toggleSidebar, setSidebarState } = useUIStore();
  const isExpanded = sidebarState === 'expanded';
  const isCollapsed = sidebarState === 'collapsed';
  const isHidden = sidebarState === 'hidden';

  // Atlas-bound modules; useModules already applies role filter and order
  const { modules } = useModules({ filterByRole: true, dashboardOnly: false });

  const ToggleIcon = toggleIcons[sidebarState];

  return (
    <>
      <aside
        className={cn(
          'fixed left-0 top-0 z-30 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900',
          isExpanded && 'w-64 translate-x-0',
          isCollapsed && 'w-16 translate-x-0',
          isHidden && 'w-64 -translate-x-full',
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-700">
          {isExpanded && (
            <span className="text-lg font-bold text-primary-700 dark:text-primary-400">Elmes</span>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            title={toggleTitles[sidebarState]}
          >
            <ToggleIcon className="h-5 w-5 transition-transform" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {/* Dashboard — static meta-overview, not a module */}
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                isCollapsed && 'justify-center px-2',
              )
            }
            title={isCollapsed ? 'Dashboard' : undefined}
          >
            <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
            {isExpanded && <span>Dashboard</span>}
          </NavLink>

          {modules.map((mod) => (
            <NavLink
              key={mod.slug}
              to={mod.routePath}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
                  isCollapsed && 'justify-center px-2',
                )
              }
              title={isCollapsed ? mod.title : undefined}
            >
              <ModuleIcon name={mod.iconName} className="h-5 w-5 flex-shrink-0" />
              {isExpanded && <span>{mod.title}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Version & build info */}
        <div className={cn('shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 py-2', isCollapsed && 'px-1 text-center')}>
          {isExpanded ? (
            <>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Elmes v{__APP_VERSION__} &middot; Build #{__APP_BUILD__}
              </p>
              {__APP_BUILD_TIME__ && (
                <p className="text-[10px] text-gray-300 dark:text-gray-600">
                  {new Date(__APP_BUILD_TIME__).toLocaleDateString('tr-TR')}{' '}
                  {new Date(__APP_BUILD_TIME__).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </>
          ) : (
            <p className="text-[10px] text-gray-400 dark:text-gray-500">#{__APP_BUILD__}</p>
          )}
        </div>
      </aside>

      {/* Floating reveal button — visible only when sidebar is hidden */}
      {isHidden && (
        <button
          onClick={() => setSidebarState('expanded')}
          className="fixed left-3 top-3 z-40 rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-md transition-all hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          title="Sidebar'ı aç"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
